// frontend/src/pages/Templates/PortalTemplates.jsx
// Read-only template view for vendor and partner users
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getTemplates, getVersionDownloadUrl } from "../../api/template.api.js";
import { API_URL } from "../../api/client.js";
import {
  PageHeader,
  Spinner,
  EmptyState,
  formatDate,
  formatFileSize,
} from "../../components/ui";
import { FileText, Download, Users, ChevronDown, ChevronUp, Eye } from "lucide-react";
import DocPreviewModal from "../../components/DocPreviewModal.jsx";

const CATEGORIES = ["NDA", "MOU", "PKS", "KONTRAK", "ADDENDUM", "GENERAL"];

const CATEGORY_COLORS = {
  NDA: "bg-purple-50 text-purple-700 border-purple-200",
  MOU: "bg-blue-50 text-blue-700 border-blue-200",
  PKS: "bg-green-50 text-green-700 border-green-200",
  KONTRAK: "bg-amber-50 text-amber-700 border-amber-200",
  ADDENDUM: "bg-orange-50 text-orange-700 border-orange-200",
  GENERAL: "bg-gray-50 text-gray-600 border-gray-200",
};

const MIME_LABELS = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
};

// ─── Template row with expandable version list ─────────────────────────────────

function TemplateRow({ template }) {
  const [expanded, setExpanded] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(null);

  const latest = template.versions?.[0];
  const versionCount = template._count?.versions ?? 0;

  async function handleDownload(versionId, fileName) {
    setDownloadingId(versionId);
    try {
      const { url } = await getVersionDownloadUrl(template.id, versionId);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.target = "_blank";
      a.click();
    } catch (err) {
      alert(err.message || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="card overflow-hidden">
      <div
        className="flex items-start gap-4 px-6 py-4 cursor-pointer hover:bg-gray-50/60 transition-colors"
        onClick={() => setExpanded((x) => !x)}
      >
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center">
            <FileText size={16} className="text-brand-500" />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900">{template.title}</h3>
            <span
              className={`text-xs font-medium px-1.5 py-0.5 rounded border ${
                CATEGORY_COLORS[template.category] ?? CATEGORY_COLORS.GENERAL
              }`}
            >
              {template.category}
            </span>
          </div>
          {template.description && (
            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{template.description}</p>
          )}
          <div className="flex items-center gap-3 mt-1.5">
            <span className="text-xs text-gray-400">
              {versionCount} version{versionCount !== 1 ? "s" : ""}
              {latest && ` · Latest: ${latest.versionLabel} (${formatDate(latest.uploadedAt)})`}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {latest && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPreviewVersion({ id: latest.id, fileName: latest.fileName, mimeType: latest.mimeType, fileSize: latest.fileSize });
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                title="Preview"
              >
                <Eye size={15} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(latest.id, latest.fileName);
                }}
                disabled={downloadingId === latest.id}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-50 text-brand-600 hover:bg-brand-100 transition-colors"
              >
                {downloadingId === latest.id ? (
                  <span className="w-3.5 h-3.5 border-2 border-brand-300 border-t-brand-600 rounded-full animate-spin" />
                ) : (
                  <Download size={13} />
                )}
                Download
              </button>
            </>
          )}
          {expanded ? (
            <ChevronUp size={15} className="text-gray-400" />
          ) : (
            <ChevronDown size={15} className="text-gray-400" />
          )}
        </div>
      </div>

      {/* Version list */}
      {expanded && versionCount > 0 && (
        <div className="border-t border-gray-100 divide-y divide-gray-50 bg-gray-50/40">
          {template.versions?.map((v, idx) => (
            <div key={v.id} className="flex items-center gap-4 px-6 py-3 pl-16">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{v.versionLabel}</span>
                  {idx === 0 && (
                    <span className="text-xs bg-brand-50 text-brand-600 px-1.5 py-0.5 rounded font-medium">
                      Latest
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    {MIME_LABELS[v.mimeType] ?? v.mimeType} · {formatFileSize(v.fileSize)}
                  </span>
                </div>
                {v.notes && <p className="text-xs text-gray-400 mt-0.5 italic">{v.notes}</p>}
                <p className="text-xs text-gray-400 mt-0.5">{formatDate(v.uploadedAt)}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setPreviewVersion({ id: v.id, fileName: v.fileName, mimeType: v.mimeType, fileSize: v.fileSize })}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                  title="Preview"
                >
                  <Eye size={14} />
                </button>
                <button
                  onClick={() => handleDownload(v.id, v.fileName)}
                  disabled={downloadingId === v.id}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                >
                  {downloadingId === v.id ? (
                    <span className="w-4 h-4 border-2 border-gray-300 border-t-brand-500 rounded-full animate-spin inline-block" />
                  ) : (
                    <Download size={14} />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {previewVersion && (
        <DocPreviewModal
          fileName={previewVersion.fileName}
          mimeType={previewVersion.mimeType}
          fileSize={previewVersion.fileSize}
          getUrl={() => getVersionDownloadUrl(template.id, previewVersion.id)}
          streamUrl={`${API_URL}/templates/${template.id}/versions/${previewVersion.id}/stream`}
          onClose={() => setPreviewVersion(null)}
        />
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function PortalTemplates() {
  const [categoryFilter, setCategoryFilter] = useState("");

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["portal-templates", categoryFilter],
    queryFn: () =>
      getTemplates({
        ...(categoryFilter && { category: categoryFilter }),
      }),
  });

  return (
    <div>
      <PageHeader
        title="Template Dokumen"
        subtitle="Download template dokumen yang tersedia"
      />

      <div className="flex gap-1.5 flex-wrap mb-6">
        <button
          onClick={() => setCategoryFilter("")}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            !categoryFilter
              ? "bg-brand-600 text-white"
              : "bg-white border border-gray-200 text-gray-600 hover:border-brand-400"
          }`}
        >
          Semua
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCategoryFilter(c === categoryFilter ? "" : c)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              categoryFilter === c
                ? "bg-brand-600 text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:border-brand-400"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Spinner />
      ) : templates.length === 0 ? (
        <EmptyState
          title="Belum ada template"
          description={
            categoryFilter
              ? `Tidak ada template untuk kategori "${categoryFilter}".`
              : "Template dokumen akan tersedia di sini."
          }
        />
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <TemplateRow key={t.id} template={t} />
          ))}
        </div>
      )}
    </div>
  );
}
